import React, { useEffect } from "react";
import { X } from "lucide-react";
import QuackManGame from "./QuackManGame";

/**
 * Modal wrapper for the QuackMan arcade game.
 * - Only mounts <QuackManGame /> when open (so the game loop only runs
 *   while the modal is visible — no wasted CPU when closed).
 * - Locks body scroll while open, unlocks on close.
 * - Closes on ESC, on backdrop click, and via the top-right X.
 */
export default function QuackManModal({ open, onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center cq-quack-modal"
      role="dialog"
      aria-modal="true"
      aria-label="QuackMan arcade game"
      data-testid="quackman-modal"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close QuackMan"
        onClick={onClose}
        className="absolute inset-0 bg-slate-950/80 backdrop-blur-md cq-quack-modal-bg"
        data-testid="quackman-modal-backdrop"
      />
      {/* Cabinet */}
      <div
        className="relative z-10 w-full max-w-4xl max-h-[95vh] overflow-y-auto px-3 py-4 sm:px-6 sm:py-6 cq-quack-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 sm:top-5 sm:right-5 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur text-white flex items-center justify-center transition"
          aria-label="Close game"
          data-testid="quackman-modal-close"
        >
          <X size={18} />
        </button>
        <QuackManGame />
      </div>

      <style>{`
        .cq-quack-modal { animation: cq-quack-modal-in 260ms cubic-bezier(0.22,1,0.36,1) both; }
        .cq-quack-modal-bg { animation: cq-quack-fade 240ms ease-out both; }
        .cq-quack-modal-panel { animation: cq-quack-scale 320ms cubic-bezier(0.34,1.56,0.64,1) both; }
        @keyframes cq-quack-modal-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cq-quack-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes cq-quack-scale {
          0%   { opacity: 0; transform: scale(0.85) translateY(20px); }
          70%  { opacity: 1; transform: scale(1.02) translateY(-4px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
